import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { GoogleMap, useLoadScript, Circle, OverlayView, InfoWindow } from '@react-google-maps/api';
import {
  Alert,
  Box,
  CircularProgress,
  Typography,
  useTheme,
} from '@mui/material';
import { deriveNeighbourDbm, EVIL_TWIN_SURVEY_MAX_AGE_HOURS, getSurveyInterferenceColor } from '../../utils/acsSurveyMapUtils';

const GOOGLE_MAPS_API_KEY = 'AIzaSyDBzNIMvmobitJ-vi8Z-kzVfK99qSb7jRw';
const GOOGLE_MAPS_LIBRARIES = ['geometry', 'marker'];

const defaultCenter = { lat: 32.0853, lng: 34.7818 };

const SurveyLabel = ({ marker, strokeColor }) => (
  <Box
    sx={{
      display: 'inline-flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: 46,
      px: 0.6,
      py: 0.35,
      borderRadius: marker.isForeign ? 0.5 : 1,
      bgcolor: marker.isFresh ? 'rgba(255, 255, 255, 0.94)' : 'rgba(240, 240, 240, 0.92)',
      border: marker.isForeign ? '1.5px dashed' : '1.5px solid',
      borderColor: strokeColor,
      boxShadow: '0 1px 4px rgba(0,0,0,0.22)',
      pointerEvents: 'none',
      userSelect: 'none',
      lineHeight: 1.1,
      textAlign: 'center',
      opacity: marker.isFresh ? 1 : 0.72,
    }}
  >
    <Typography component="span" sx={{ fontSize: '0.82rem', fontWeight: 800, color: '#111' }}>
      ch {marker.channel}
    </Typography>
    {marker.frequencyMHz ? (
      <Typography component="span" sx={{ fontSize: '0.62rem', fontWeight: 600, color: '#333' }}>
        {marker.frequencyMHz} MHz
      </Typography>
    ) : null}
    <Typography
      component="span"
      sx={{
        fontSize: '0.58rem',
        fontWeight: 700,
        color: marker.isEvilTwin ? '#b71c1c' : (marker.isForeign ? '#6a1b9a' : '#555'),
        fontFamily: marker.isForeign ? 'inherit' : 'monospace',
      }}
    >
      {marker.isEvilTwin || marker.isForeign ? marker.ssidLabel : marker.serialSuffix}
    </Typography>
  </Box>
);

const ChannelSurveyMapView = ({
  markers,
  markerRadiusM,
  height = 520,
  selectedMarkerId,
  onSelectMarker,
  emptyMessage,
}) => {
  const theme = useTheme();
  const mapRef = useRef(null);
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const mapColorScheme = useMemo(() => {
    if (!isLoaded || !window.google?.maps?.ColorScheme) return undefined;
    return theme.palette.mode === 'dark'
      ? window.google.maps.ColorScheme.DARK
      : window.google.maps.ColorScheme.LIGHT;
  }, [isLoaded, theme.palette.mode]);

  const mapCenter = useMemo(() => {
    if (!markers.length) return defaultCenter;
    const lat = markers.reduce((sum, m) => sum + m.lat, 0) / markers.length;
    const lng = markers.reduce((sum, m) => sum + m.lng, 0) / markers.length;
    return { lat, lng };
  }, [markers]);

  const fitToMarkers = useCallback(() => {
    const map = mapRef.current;
    if (!map || !markers.length || !window.google?.maps) return;
    const bounds = new window.google.maps.LatLngBounds();
    markers.forEach((m) => {
      bounds.extend({ lat: m.lat, lng: m.lng });
      bounds.extend({ lat: m.labelLat, lng: m.labelLng });
    });
    map.fitBounds(bounds, 48);
  }, [markers]);

  const handleMapLoad = useCallback(
    (map) => {
      mapRef.current = map;
      fitToMarkers();
    },
    [fitToMarkers],
  );

  useEffect(() => {
    fitToMarkers();
  }, [fitToMarkers]);

  if (loadError) {
    return <Alert severity="error">Failed to load Google Maps.</Alert>;
  }

  if (!isLoaded) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', height, borderRadius: 1, overflow: 'hidden', border: 1, borderColor: 'divider' }}>
      {!markers.length ? (
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'action.hover',
          }}
        >
          <Typography color="text.secondary" sx={{ px: 2, textAlign: 'center' }}>
            {emptyMessage || 'No ACS survey points to plot.'}
          </Typography>
        </Box>
      ) : (
        <GoogleMap
          mapContainerStyle={{ width: '100%', height: '100%' }}
          center={mapCenter}
          zoom={12}
          onLoad={handleMapLoad}
          options={{
            streetViewControl: false,
            mapTypeControl: true,
            fullscreenControl: true,
            mapId: 'RRM_ACS_SURVEY_MAP',
            colorScheme: mapColorScheme,
          }}
        >
          {markers.map((marker) => {
            const color = marker.isEvilTwin
              ? '#c62828'
              : marker.isForeign
                ? '#7b1fa2'
                : getSurveyInterferenceColor(marker.chLoad, marker.maxRssi);
            const circleCenter = { lat: marker.lat, lng: marker.lng };
            const labelPosition = { lat: marker.labelLat, lng: marker.labelLng };
            const fillOpacity = marker.isEvilTwin
              ? 0.22
              : marker.isForeign
                ? (marker.isFresh ? 0.14 : 0.08)
                : (marker.isFresh ? 0.2 : 0.1);
            return (
              <React.Fragment key={marker.id}>
                <Circle
                  center={circleCenter}
                  radius={markerRadiusM}
                  options={{
                    fillColor: color,
                    fillOpacity,
                    strokeColor: color,
                    strokeOpacity: marker.isEvilTwin ? 0.9 : (marker.isFresh ? 0.75 : 0.45),
                    strokeWeight: marker.isEvilTwin ? 2.5 : (marker.isForeign ? 1.5 : (marker.isFresh ? 2 : 1.2)),
                    clickable: true,
                  }}
                  onClick={() => onSelectMarker?.(marker.id)}
                />
                <OverlayView
                  position={labelPosition}
                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                  getPixelPositionOffset={(width, height) => ({
                    x: -(width / 2),
                    y: -(height / 2),
                  })}
                >
                  <SurveyLabel marker={marker} strokeColor={color} />
                </OverlayView>
              </React.Fragment>
            );
          })}

          {selectedMarkerId && (() => {
            const selectedMarker = markers.find((m) => m.id === selectedMarkerId);
            if (!selectedMarker) return null;
            const neighbours = (selectedMarker.neighbours || []).slice(0, 8);
            return (
              <InfoWindow
                position={{ lat: selectedMarker.labelLat, lng: selectedMarker.labelLng }}
                onCloseClick={() => onSelectMarker?.(null)}
              >
                <Box sx={{ minWidth: 220, maxWidth: 320, color: '#000' }}>
                  {selectedMarker.isEvilTwin ? (
                    <>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#b71c1c' }}>
                        Possible evil twin · {selectedMarker.ssid}
                      </Typography>
                      <Typography variant="body2">
                        {selectedMarker.band} · ch {selectedMarker.channel}
                      </Typography>
                      <Typography variant="caption" display="block">
                        BSSID {selectedMarker.foreignBssid || selectedMarker.bssid}
                        {selectedMarker.rssiDbm != null ? ` · ${Math.round(selectedMarker.rssiDbm)} dBm` : ''}
                      </Typography>
                      {selectedMarker.expectedOwnerSerials?.length ? (
                        <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                          Expected fleet owner: {selectedMarker.expectedOwnerSerials.join(', ')}
                        </Typography>
                      ) : null}
                      <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                        Corroborated by {selectedMarker.hearerCount || selectedMarker.hearings?.length || 1} hearer(s).
                        Heard strongest by {selectedMarker.heardBySerial} near this GPS (approximate).
                      </Typography>
                      {!selectedMarker.isFresh ? (
                        <Typography variant="caption" display="block" color="warning.dark">
                          Survey older than {EVIL_TWIN_SURVEY_MAX_AGE_HOURS} h
                        </Typography>
                      ) : null}
                    </>
                  ) : selectedMarker.isForeign ? (
                    <>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        External AP · {selectedMarker.ssid}
                      </Typography>
                      <Typography variant="body2">
                        {selectedMarker.band} · ch {selectedMarker.channel}
                        {selectedMarker.frequencyMHz ? ` (${selectedMarker.frequencyMHz} MHz)` : ''}
                      </Typography>
                      <Typography variant="caption" display="block">
                        BSSID {selectedMarker.bssid}
                        {selectedMarker.rssiDbm != null ? ` · ${Math.round(selectedMarker.rssiDbm)} dBm` : ''}
                      </Typography>
                      <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                        Heard by {selectedMarker.heardBySerial} at this GPS (approximate placement).
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {selectedMarker.serial}
                        {selectedMarker.isCap ? ' (CAP)' : ''}
                      </Typography>
                      <Typography variant="body2">
                        {selectedMarker.band} · ch {selectedMarker.channel}
                        {selectedMarker.frequencyMHz ? ` (${selectedMarker.frequencyMHz} MHz)` : ''}
                      </Typography>
                      <Typography variant="caption" display="block">
                        Load {selectedMarker.chLoad}% · BSS {selectedMarker.bss}
                        {selectedMarker.maxRssi < 0 ? ` · max ${selectedMarker.maxRssi} dBm` : ''}
                        {selectedMarker.noiseFloor < -50 ? ` · NF ${selectedMarker.noiseFloor} dBm` : ''}
                      </Typography>
                    </>
                  )}
                  {!selectedMarker.isFresh ? (
                    <Typography variant="caption" display="block" color="warning.dark">
                      Survey older than 12 h
                    </Typography>
                  ) : null}
                  {!selectedMarker.isForeign && neighbours.length > 0 ? (
                    <>
                      <Typography variant="caption" display="block" sx={{ mt: 0.75, fontWeight: 700 }}>
                        SSIDs heard ≥ min signal
                      </Typography>
                      {neighbours.map((n, idx) => {
                        const ssid = n.Neighbour_ssid || n.neighbour_ssid || n.SSID || n.ssid || '—';
                        const bssid = n.BSSID || n.bssid || '';
                        const dbm = deriveNeighbourDbm(n, selectedMarker.noiseFloor);
                        return (
                          <Typography key={`${bssid}-${idx}`} variant="caption" display="block">
                            {ssid}
                            {dbm != null ? ` (${Math.round(dbm)} dBm)` : ''}
                          </Typography>
                        );
                      })}
                    </>
                  ) : !selectedMarker.isForeign && selectedMarker.neighbourCountTotal > 0 ? (
                    <Typography variant="caption" display="block" sx={{ mt: 0.75 }} color="text.secondary">
                      No BSSIDs heard at or above the current min-signal floor on this channel.
                    </Typography>
                  ) : null}
                </Box>
              </InfoWindow>
            );
          })()}
        </GoogleMap>
      )}
    </Box>
  );
};

export default ChannelSurveyMapView;
